import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useApi } from "../hooks/useApi";
import { gbpShort } from "../lib/format";
import { Loading, PageHeader } from "../components/common";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";

function InviteDialog({ open, onClose, onDone }) {
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "" });
  const [err, setErr] = useState("");
  const go = async () => { setErr(""); try { await api.post("/onboarding/invite", f); toast.success("Invite sent"); onDone(); onClose(); } catch (e) { setErr(errMsg(e)); } };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent><DialogHeader><DialogTitle className="font-heading">Invite a JV partner</DialogTitle></DialogHeader>
        {[["company_name", "Company name"], ["contact_name", "Contact name"], ["email", "Email"]].map(([k, l]) => <div key={k} className="space-y-1"><Label className="text-xs">{l}</Label>
          <Input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`invite-${k}`} /></div>)}
        {err && <div className="text-sm font-medium text-primary" data-testid="invite-error">{err}</div>}
        <DialogFooter><Button onClick={go} data-testid="invite-send-btn">Send invite</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Column({ title, cards, link, tid }) {
  return (
    <div className="min-w-[170px] flex-1 rounded-lg bg-muted/50 p-2" data-testid={tid}>
      <div className="mb-2 flex items-center justify-between px-1"><span className="eyebrow">{title}</span><span className="font-figure text-xs">{cards.length}</span></div>
      <div className="space-y-2">{cards.map((c) => <Link key={c.id} to={link(c)} className="block rounded-md border bg-card p-3 transition-colors hover:border-primary" data-testid={`board-card-${c.id}`}>
        <div className="font-semibold leading-tight">{c.name}</div>
        {c.partner_name && <div className="text-xs text-muted-foreground">{c.partner_name}{c.equity ? ` · ${gbpShort(c.equity)}` : ""}</div>}
        <div className="mt-2 text-xs"><span className="text-muted-foreground">Waiting on </span><b data-testid="board-waiting-on">{c.waiting_on}</b></div>
        <div className="text-xs text-muted-foreground" data-testid="board-days">{c.days_waiting} day(s) waiting</div>
        {c.flags?.length > 0 && <div className="mt-1 text-[11px] font-semibold text-primary">Red flag</div>}
      </Link>)}</div>
    </div>
  );
}

export default function Board() {
  const { data, loading, reload } = useApi("/onboarding/board");
  const [inv, setInv] = useState(false);
  if (loading && !data) return <Loading />;
  return (
    <div className="space-y-8" data-testid="onboarding-board-page">
      <PageHeader eyebrow="Onboarding" title="Onboarding board" subtitle="New JV partners and new projects, by stage – with who each is waiting on."
        actions={<><Button variant="outline" asChild><Link to="/opportunities/new" data-testid="board-new-opportunity">New project opportunity</Link></Button>
          <Button onClick={() => setInv(true)} data-testid="invite-partner-btn"><Plus className="mr-1 h-4 w-4" />Invite partner</Button></>} />
      <section><h2 className="mb-3 font-heading text-lg font-semibold">Partners</h2>
        <div className="flex gap-3 overflow-x-auto pb-2 grid-scroll" data-testid="board-partners">{[...data.partner_stages, "Rejected"].map((s) => <Column key={s} title={s} tid={`board-partner-col-${s.replace(/\W+/g, "-").toLowerCase()}`}
          cards={data.partners.filter((p) => p.stage === s)} link={(c) => `/onboarding/partners/${c.id}`} />)}</div></section>
      <section><h2 className="mb-3 font-heading text-lg font-semibold">Projects</h2>
        <div className="flex gap-3 overflow-x-auto pb-2 grid-scroll" data-testid="board-projects">{[...data.project_stages, "Rejected"].map((s) => <Column key={s} title={s} tid={`board-project-col-${s.replace(/\W+/g, "-").toLowerCase()}`}
          cards={data.projects.filter((p) => p.stage === s)} link={(c) => `/pipeline/${c.id}`} />)}</div></section>
      <InviteDialog open={inv} onClose={() => setInv(false)} onDone={() => reload(true)} />
    </div>
  );
}
